import {isNull, omitBy, uniqBy} from 'lodash';

import {BLOCKING_SERIAL_ERRORS} from '../components/ScanSerials/constants';
import {LAST_TRANSACTIONS_STORED} from '../constants';
import {AsyncState, Model, ReceiveStatus, ReceiveType, ReferenceQueryResponseItem, Serial, State} from './types';
import {WidgetType} from "../widgets/widgetCommon/widgetConstants";

export enum Types {
  scanReference = 'SCAN_REFERENCE_ID',
  activeReference = 'ACTIVE_REFERENCE',
  clearReference = 'CLEAR_SCAN_REFERENCE_ID',
  scanTrackingId = 'SCAN_TRACKING_ID',

  setInitialShipmentQuery = 'SET_SHIPMENT_QUERY',
  setInitialInventoryAdjustment = 'SET_INVENTORY_ADJUSTMENT',

  updateSiteName = 'UPDATE_SITE_NAME',
  listModels = 'LIST_MODELS',
  scanModel = 'SCAN_MODEL',
  updateMpnMapping = 'UPDATE_MPN_MAPPING',
  deleteModel = 'DELETE_MODEL',

  scanContainer = 'SCAN_CONTAINER',
  moveContainer = 'MOVE_CONTAINER',
  deleteContainer = 'DELETE_CONTAINER',

  scanSerial = 'SCAN_SERIAL',
  scanSerialComplete = 'SCAN_SERIAL_COMPLETE',
  deleteSerial = 'DELETE_SERIAL',
  resetScans = 'RESET_SCANS',
  errorSerial = 'ERROR_SERIAL',
  siteSpecificSerialScanned = 'SITE_SPECIFIC_SERIAL_SCANNED',

  receiveParts = 'RECEIVE_PARTS',
  receivePartsInModel = 'RECEIVE_PARTS_MODEL_SUCCESSFUL',
  addToExecutions = 'ADD_TO_EXECUTIONS_LIST',
  updateExecutions = 'UPDATE_EXECUTIONS_LIST',

  removeInProgress = 'REMOVE_IN_PROGRESS',
  syncState = 'SYNC_STATE',
  updateBuildID = 'UPDATE_BUILD_ID',
  updateCategory = 'UPDATE_CATEGORY',
  updateSite = 'UPDATE_SITE',
  updateTags = 'UPDATE_TAGS',
  updatePermissionsForIA = 'UPDATE_PERMISSIONS_FOR_IA',
  updatePermissionsForReceive = 'UPDATE_PERMISSIONS_FOR_RECEIVE',
}

export const Reducer = (state: State, action: { type: string; payload: any }): State => {
  const models: Model[] = state?.models || [];

  switch (action.type) {
    case Types.scanReference:
      const ref = { ...state.activeReference, ...action?.payload };
      const refErrors = ref?.errors || [];

      const validReference =
        !ref?.loading && !ref?.error && refErrors.length === 0 && ref?.data?.receiveReferenceQueryResponseList?.[0];
      const receiveReference: ReferenceQueryResponseItem | null = validReference
        ? {
            ...ref?.data?.receiveReferenceQueryResponseList?.[0],
            shipmentMissingReasonCode: ref?.data?.shipmentMissingReasonCode,
          }
        : null;
      const isRma = receiveReference?.receiveReference.externalReferenceType === ReceiveType.ASN_RMA;

      const receiveReferenceExists =
        receiveReference &&
        state?.receiveReferences?.find((ref: ReferenceQueryResponseItem) => {
          if (isRma) {
            return (
              ref?.receiveReference?.externalReferenceValue === receiveReference.receiveReference.externalReferenceValue
            );
          }
          return ref?.receiveReference?.receiveReferenceId === receiveReference.receiveReference.receiveReferenceId;
        });

      return {
        ...state,
        receiveReference: ref,
        receiveReferences:
          !receiveReferenceExists && receiveReference
            ? state?.receiveReferences?.concat(receiveReference)
            : state?.receiveReferences?.map((reference) =>
                reference.receiveReference.receiveReferenceId ===
                receiveReferenceExists?.receiveReference.receiveReferenceId
                  ? receiveReference
                  : reference,
              ),
      };
    case Types.addToExecutions:
      const executionExists = state?.executions?.find((e) => e?.mpnIpnId === action?.payload?.mpnIpnId);
      if (executionExists) {
        return state;
      }
      const sortedExecutions =
        state?.executions?.sort((e1, e2) => e2?.receiveScanEndTime - e1?.receiveScanEndTime) || [];
      const totalExecutions =
        sortedExecutions?.length >= LAST_TRANSACTIONS_STORED
          ? sortedExecutions?.slice(0, LAST_TRANSACTIONS_STORED - 1)
          : sortedExecutions;
      const newExecutions = totalExecutions?.concat(action?.payload);

      return {
        ...state,
        executions: newExecutions,
      };
    case Types.updateExecutions:
      const activeMpnIPNIDS = state?.executions?.map((e) => e?.mpnIpnId);
      const activeRefernceIds = state?.executions?.map((e) => e?.receiveReferenceId);
      return {
        ...state,
        executions: state?.executions?.map((e) =>
          e?.mpnIpnId === action?.payload?.mpnIpnId
            ? {
                ...e,
                receiveExecutionUUId: action?.payload?.receiveExecutionUUId,
                status: action?.payload?.status,
              }
            : e,
        ),
        serials: state?.serials?.filter((s) => {
          const mpnIpnID = s?.mpnIpnId;
          if (!mpnIpnID) {
            return false;
          }
          return activeMpnIPNIDS?.includes(mpnIpnID);
        }),
        receiveReferences: state?.receiveReferences?.filter((ref) => {
          const activeRefernceId = ref?.receiveReference?.receiveReferenceId;
          if (!activeRefernceId) {
            return false;
          }
          return activeRefernceIds?.includes(activeRefernceId);
        }),
        models: state?.models?.filter((m) => {
          const mpnIpnID = m?.mpnIpnId;
          if (!mpnIpnID) {
            return false;
          }
          return activeMpnIPNIDS?.includes(mpnIpnID);
        }),
      };

    case Types.activeReference:
      return {
        ...state,
        activeReference: action?.payload,
      };
    case Types.clearReference:
      return {
        ...state,
        receiveReference: undefined,
      };
    case Types.scanTrackingId:
      return {
        ...state,
        receiveReferences: state?.receiveReferences?.map((ref) => {
          return action?.payload?.referenceId === ref?.receiveReference?.receiveReferenceId
            ? {
                ...ref,
                receiveReference: { ...ref.receiveReference, trackingId: action?.payload?.trackingId },
              }
            : ref;
        }),
      };

    // [TYPE] - AWS Boost Shipment reference - resets state for every new shipmentid
    case Types.setInitialShipmentQuery:
      if (
        action?.payload?.initialShipmentReferenceId !== state?.initialShipmentReferenceId &&
        action?.payload?.initialPackagingId !== state?.initialPackagingId &&
        state?.initialPackagingId &&
        state?.initialShipmentReferenceId
      ) {
        return {
          ...state,
          receiveReference: {
            errors: [],
            loading: false,
            data: [],
          },
          receiveReferences: [],
          shouldRefetchReference: true,
        };
      }

      const reference: ReferenceQueryResponseItem | null =
        state?.receiveReference?.data?.receiveReferenceQueryResponseList?.[0];

      const shipmentMissingReasonCodeSelected =
        reference?.receiveReference.externalReferenceValue === action?.payload?.initialShipmentReferenceId &&
        !!state?.receiveReference?.data?.shipmentMissingReasonCode;
      const isExistingRMAReference =
        reference?.receiveReference.externalReferenceType === ReceiveType.ASN_RMA &&
        reference?.receiveReference.externalReferenceValue === action?.payload?.initialShipmentReferenceId;

      // if not RMA reference always refetching
      // if shipment code is already selected from multiple references found in a shipment, not refecting the reference
      const shouldRefetchReference = !isExistingRMAReference && !shipmentMissingReasonCodeSelected;

      return {
        ...state,
        receiveReference: shouldRefetchReference
          ? {
              errors: [],
              loading: false,
              data: [],
            }
          : state?.receiveReference,
        receiveReferences: shouldRefetchReference ? [] : state?.receiveReferences,
        initialShipmentReferenceId: action?.payload?.initialShipmentReferenceId,
        initialPackagingId: action?.payload?.initialPackagingId,
        workRequestId: action?.payload?.workRequestId,
        shouldRefetchReference,
        initialWorkRequestlocationId: action?.payload?.locationId,
      };
      case Types.setInitialInventoryAdjustment:
          const workRequestId = action?.payload?.workRequestId
          if (
              workRequestId !== state?.workRequestId
          ) {
              return {
                  ...state,
                  receiveReference: {
                      errors: [],
                      loading: false,
                      data: [],
                  },
                  receiveReferences: [],
                  shouldRefetchReference: true,
                  widgetType: WidgetType.INVENTORY_ADJUSTMENT,
                  workRequestId: workRequestId
              };
          }

          return {
              ...state,
              widgetType: WidgetType.INVENTORY_ADJUSTMENT,
              workRequestId: action?.payload?.workRequestId,
          };
    case Types.updateSiteName:
      return {
        ...state,
        receiveReferences: state?.receiveReferences?.map((ref) => {
          return action?.payload?.referenceId === ref?.receiveReference?.receiveReferenceId
            ? {
                ...ref,
                receiveReference: {
                  ...ref.receiveReference,
                  siteName: action?.payload?.siteName,
                  expectedReceiveLocation: action?.payload?.siteId,
                },
              }
            : ref;
        }),
      };
    case Types.scanModel:
      return {
        ...state,
        models: models?.concat({ ...action?.payload, containerIds: [] }),
      };
    case Types.updateBuildID:
      const buildId = action?.payload?.buildId;
      return {
        ...state,
        models: state.models?.map((model: Model) =>
          model.mpnIpnId === action?.payload.id
            ? {
                ...model,
                buildId: buildId,
              }
            : model,
        ),
      };
    case Types.updateCategory:
      const category = action?.payload?.category;
      return {
        ...state,
        models: state.models?.map((model: Model) =>
          model.mpnIpnId === action?.payload.id
            ? {
                ...model,
                buildCategory: category,
              }
            : model,
        ),
      };
    case Types.updateTags:
      const tagList = action?.payload?.tags ? action?.payload?.tags : [];
      return {
        ...state,
        models: state.models?.map((model: Model) =>
          model.mpnIpnId === action?.payload.id
            ? {
                ...model,
                tagList,
              }
            : model,
        ),
      };
    case Types.updateSite:
      const siteName = action?.payload?.siteName;
      return {
        ...state,
        models: state.models?.map((model: Model) =>
          model.mpnIpnId === action?.payload.id
            ? {
                ...model,
                siteName: siteName,
              }
            : model,
        ),
      };
    case Types.deleteModel:
      const selectedMpnIpnIds = action?.payload?.mpnIpnIds || [];
      return {
        ...state,
        models: models?.filter((model: Model) => !selectedMpnIpnIds.includes(model.mpnIpnId)),
        executions: state?.executions?.filter((e) => !selectedMpnIpnIds.includes(e.mpnIpnId)),
        serials: state?.serials?.filter((serial: Serial) => !selectedMpnIpnIds.includes(serial.mpnIpnId)),
      };
    case Types.updateMpnMapping:
      return {
        ...state,
        models: models?.map((model: Model) =>
          action?.payload?.mpnIpnId === model?.mpnIpnId
            ? {
                ...model,
                ...action?.payload,
              }
            : model,
        ),
      };
    case Types.receivePartsInModel:
      const succeededMpnIpn = state.models?.find(
        (model: Model) => model.receiveExecutionUUId === action?.payload.receiveExecutionUUId,
      );
      const serialsProcessed = action?.payload?.serialsProcessed || {};

      if (!succeededMpnIpn) {
        return state;
      }
      return {
        ...state,
        models: state.models?.map((model: Model) =>
          model.receiveExecutionUUId === action?.payload.receiveExecutionUUId
            ? {
                ...model,
                status: action?.payload?.status,
                receivedErrorsList: action?.payload?.receivedErrorsList || [],
              }
            : model,
        ),
        serials: state?.serials?.map((serial: Serial) => {
          if (succeededMpnIpn.mpnIpnId !== serial.mpnIpnId) {
            return serial;
          }
          const serialPayload = serialsProcessed?.[serial.serialNumber];
          const serialStatus: ReceiveStatus = {
            receiveStatusType:
              action?.payload?.status === AsyncState.OK
                ? AsyncState.OK
                : serialPayload?.receiveState || AsyncState.IN_PROGRESS,
            statusCode: action?.payload?.status === AsyncState.ERROR ? serialPayload?.errorCode : undefined,
          };

          return { ...serial, receiveStatus: serialStatus };
        }),
        executions: state?.executions?.map((e) => {
          if (e?.receiveExecutionUUId === action?.payload.receiveExecutionUUId) {
            return { ...e, status: action?.payload?.status };
          }

          return e;
        }),
      };
    case Types.scanContainer:
      return {
        ...state,
        models: models.map((model: Model) =>
          model.mpnIpnId === action?.payload.modelId
            ? { ...model, containerIds: model?.containerIds?.concat(action?.payload.containerId) }
            : model,
        ),
        containers: {
          ...state.containers,
          [action?.payload.containerId]: {
            type: action?.payload?.containerType,
            cmsSiteLaunched: action?.payload?.cmsSiteLaunched,
          },
        },
      };
    case Types.moveContainer:
      const newContainers = {
        ...state.containers,
        [action?.payload.containerId]: undefined,
        [action?.payload.newContainerId]: {
          type: action?.payload?.containerType,
          cmsSiteLaunched: action?.payload?.cmsSiteLaunched,
        },
      };
      const newModels = state?.models?.map((model: Model) => {
        if (model.mpnIpnId === action?.payload.modelId)
          return {
            ...model,
            containerIds: model?.containerIds?.map((id) =>
              id === action?.payload.containerId ? action?.payload.newContainerId : id,
            ),
          };

        return model;
      });
      return {
        ...state,
        models: newModels,
        serials: state?.serials?.map((serial: Serial) =>
          serial?.containerId === action?.payload.containerId
            ? { ...serial, containerId: action?.payload.newContainerId }
            : serial,
        ),
        containers: omitBy(newContainers, isNull),
      };

    case Types.deleteContainer:
      const pendingContainers = {
        ...state.containers,
        [action?.payload.containerId]: undefined,
      };
      return {
        ...state,
        models: models.map((model: Model) =>
          model.mpnIpnId === action?.payload.modelId
            ? {
                ...model,
                containerIds: model?.containerIds?.filter((id) => id !== action?.payload.containerId),
              }
            : model,
        ),
        serials: state?.serials?.filter((s: Serial) => {
          return s.mpnIpnId !== action?.payload.modelId ? true : s.containerId !== action?.payload.containerId;
        }),
        containers: omitBy(pendingContainers, isNull),
      };

    case Types.scanSerial:
      const status = action?.payload?.status;
      return {
        ...state,
        serials: uniqBy(
          state?.serials?.concat({
            id: action?.payload.id,
            containerId: action?.payload.containerId,
            status: action?.payload?.status,
            errors: [],
            called: false,
            serialNumber: action?.payload.serialNumber,
            externalReferenceValue: action?.payload?.externalReferenceValue,
            modelId: action?.payload.modelId,
            manufacturer: action?.payload?.manufacturer,
            loading: action?.payload?.loading,
            state: action?.payload?.loading ? AsyncState.IN_PROGRESS : status?.validationStatusType,
            request: action?.payload?.request,
            referenceLineItemId: action?.payload?.referenceLineItemId,
            mpnIpnId: action?.payload?.mpnIpnId,
            metricsList: action?.payload?.metricsList || [],
          }),
          'id',
        ),
      };
    case Types.scanSerialComplete:
      const statusCode = action?.payload?.status?.statusCode;
      const newSerial = {
        id: action?.payload.id,
        containerId: action?.payload.containerId,
        status: action?.payload?.status,
        errors: [],
        serialNumber: action?.payload.serialNumber,
        modelId: action?.payload.modelId,
        loading: action?.payload?.loading,
        externalReferenceValue: action?.payload?.externalReferenceValue,
        called: action?.payload?.called,
        state: action?.payload?.status?.validationStatusType,
        referenceLineItemId: action?.payload?.referenceLineItemId,
        mpnIpnId: action?.payload?.mpnIpnId,
        siteSpecificSerialId: action?.payload?.siteSpecificSerialId,
      };

      const serials = state?.serials?.map((s: Serial) => {
        return s.id === action?.payload.id
          ? {
              id: s.id,
              mpnIpnId: s.mpnIpnId,
              containerId: action?.payload.containerId,
              status: action?.payload?.status,
              errors: [],
              serialNumber: s?.siteSpecificSerialId ? s?.serialNumber : action?.payload.serialNumber,
              modelId: action?.payload.modelId,
              loading: action?.payload?.loading,
              called: action?.payload?.called,
              state: action?.payload?.status?.validationStatusType,
              referenceLineItemId: action?.payload?.referenceLineItemId,
              externalReferenceValue: action?.payload?.externalReferenceValue,
              siteSpecificSerialId: action?.payload?.siteSpecificSerialId
                ? action?.payload?.siteSpecificSerialId
                : s?.siteSpecificSerialId,
              metricsList: action?.payload?.metric ? s?.metricsList?.concat(action?.payload?.metric) : s?.metricsList,
            }
          : s;
      });
      return {
        ...state,
        erroredWithAttentionSerial: BLOCKING_SERIAL_ERRORS.includes(statusCode) ? newSerial : null,
        serials: uniqBy(serials, 'id'),
      };
    case Types.deleteSerial:
      const idsToDelete = action?.payload.map((serial: Serial) => serial?.id);
      return {
        ...state,
        serials: state?.serials?.filter((s: Serial) => !idsToDelete.includes(s?.id)),
      };
    case Types.siteSpecificSerialScanned:
      return {
        ...state,
        serials: state?.serials?.map((s: Serial) =>
          s?.id === action?.payload?.id
            ? {
                ...s,
                siteSpecificSerialId: action?.payload?.siteSpecificSerialId,
                state: AsyncState.IN_PROGRESS,
                loading: true,
                request: action?.payload?.request,
                status: undefined,
                called: false,
              }
            : s,
        ),
      };
    case Types.errorSerial:
      return {
        ...state,
        erroredWithAttentionSerial: null,
        serials: action?.payload
          ? state?.serials?.filter((s: Serial) => s?.id !== action?.payload?.id)
          : state?.serials,
      };
    case Types.resetScans:
      return {
        ...state,
        serials: state?.serials?.filter((s: Serial) => s?.mpnIpnId !== action?.payload?.mpnIpnId),
        models: state?.models?.map((m: Model) =>
          m.mpnIpnId === action?.payload?.mpnIpnId ? { ...m, containerIds: [] } : m,
        ),
      };
    case Types.receiveParts:
      return {
        ...state,
        models: state?.models?.map((m: Model) =>
          action?.payload?.mpnIpnId === m.mpnIpnId
            ? { ...m, status: action?.payload?.status, receiveExecutionUUId: action?.payload?.receiveExecutionUUId }
            : m,
        ),
      };
    case Types.syncState:
      const payload = action?.payload;
      return {
        ...state,
        ...payload,
      };
    case Types.removeInProgress:
      const inProgessExecutionIDs = state?.executions
        ?.filter((e) => e?.status === AsyncState.IN_PROGRESS)
        .map((e) => e?.receiveExecutionUUId);
      return {
        ...state,
        executions: state?.executions?.filter((e) => e?.status !== AsyncState.IN_PROGRESS),
        models: state?.models?.filter(
          (e) => !e?.receiveExecutionUUId || !inProgessExecutionIDs.includes(e?.receiveExecutionUUId),
        ),
      };
    case Types.updatePermissionsForIA:
      return {
        ...state,
        hasPermissionsToAccessIA: action?.payload.hasPermissionsToAccessIA,
      };
    case Types.updatePermissionsForReceive:
      return {
        ...state,
        hasPermissionsToAccessReceive: action?.payload.hasPermissionsToAccessReceive,
      };
    default:
      return state;
  }
};
